// apps/client/src/engine/lighting.ts — the clock's light (DESIGN.md "Lighting presets"; the values are tuning.light, light and look 2026-10-16)
import { tuning } from '@whiskerton/content';
import { lightPresetAt, type LightPreset } from '@whiskerton/shared';
import * as THREE from 'three';

export interface LightValues { sky: THREE.Color; horizon: THREE.Color; hemiSky: THREE.Color; hemiGround: THREE.Color; hemiIntensity: number;
  sun: THREE.Color; sunIntensity: number; sunDir: THREE.Vector3; water: THREE.Color; waterOpacity: number; windowGlow: number; lamps: number }
const L = tuning.light;
type PresetTuning = (typeof L.presets)[LightPreset] | typeof L.night;
const fromTuning = (p: PresetTuning): LightValues => ({ sky: new THREE.Color(p.sky), horizon: new THREE.Color(p.horizon),
  hemiSky: new THREE.Color(p.hemiSky), hemiGround: new THREE.Color(p.hemiGround), hemiIntensity: p.hemi, sun: new THREE.Color(p.sun),
  sunIntensity: p.sunIntensity, sunDir: new THREE.Vector3(...p.sunDir).normalize(), water: new THREE.Color(p.water), waterOpacity: p.waterOpacity,
  windowGlow: p.windowGlow, lamps: p.lamps });
export const PRESETS: Readonly<Record<LightPreset, LightValues>> = {
  morning: fromTuning(L.presets.morning), midday: fromTuning(L.presets.midday), golden: fromTuning(L.presets.golden), dusk: fromTuning(L.presets.dusk),
};
/** The moonlit night, blended over the clock's presets by nightAt (spec §8.1, clarified 2026-10-16). */
export const NIGHT: Readonly<LightValues> = fromTuning(L.night);
/** The sun's direction from a toward b. Between two low suns on opposite sides (dusk into morning) a straight blend passes near zero
 *  length and swings round in a few minutes; lifting it by the length it lost carries the light over the top instead. */
function blendDir(a: THREE.Vector3, b: THREE.Vector3, t: number): THREE.Vector3 {
  const d = a.clone().lerp(b, t);
  d.y += 1 - d.length();
  return d.normalize();
}
export function blendPreset(a: LightValues, b: LightValues, t: number): LightValues {
  const n = (x: number, y: number) => x + (y - x) * t;
  return { sky: a.sky.clone().lerp(b.sky, t), horizon: a.horizon.clone().lerp(b.horizon, t), hemiSky: a.hemiSky.clone().lerp(b.hemiSky, t),
    hemiGround: a.hemiGround.clone().lerp(b.hemiGround, t), hemiIntensity: n(a.hemiIntensity, b.hemiIntensity), sun: a.sun.clone().lerp(b.sun, t),
    sunIntensity: n(a.sunIntensity, b.sunIntensity), sunDir: blendDir(a.sunDir, b.sunDir, t), water: a.water.clone().lerp(b.water, t),
    waterOpacity: n(a.waterOpacity, b.waterOpacity), windowGlow: n(a.windowGlow, b.windowGlow), lamps: n(a.lamps, b.lamps) };
}
/** How far into the night `minuteOfDay` is (0…1): eased in over rampMinutes centred on night.from, and out centred on night.until. */
export function nightAt(minuteOfDay: number): number {
  const m = ((minuteOfDay % 1440) + 1440) % 1440, half = L.night.rampMinutes / 2;
  const since = (start: number) => (((m - (start - half)) % 1440) + 1440) % 1440;    // minutes since a ramp began, wrapping midnight
  const ease = (x: number) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };
  const into = since(L.night.from), outOf = since(L.night.until), span = (L.night.until - L.night.from + 1440) % 1440;
  if (into < L.night.rampMinutes) return ease(into / L.night.rampMinutes);
  if (outOf < L.night.rampMinutes) return 1 - ease(outOf / L.night.rampMinutes);
  return into < span ? 1 : 0;
}
export function lightingAt(minuteOfDay: number): LightValues {
  const { a, b, t } = lightPresetAt(minuteOfDay);
  const out = blendPreset(PRESETS[a], PRESETS[b], t), night = nightAt(minuteOfDay);
  return night > 0 ? blendPreset(out, NIGHT, night) : out;
}
let windows: THREE.MeshStandardMaterial | null = null;
/** Warm emissive pane material; Lighting sets emissiveIntensity each frame (0 at midday, full at dusk/night). */
export function windowMaterial(): THREE.MeshStandardMaterial {
  windows ??= new THREE.MeshStandardMaterial({ color: '#FFE9B8', emissive: new THREE.Color('#FFC66B'), emissiveIntensity: 0, roughness: 0.5, metalness: 0 });
  return windows;
}
export class Lighting {
  readonly hemi = new THREE.HemisphereLight();
  readonly sun = new THREE.DirectionalLight();
  readonly sky: THREE.Mesh;
  private readonly scene: THREE.Scene;
  private readonly skyColor: { value: THREE.Color };
  private readonly horizonColor: { value: THREE.Color };
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    const S = L.shadow;
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(tuning.render.shadowMapHigh, tuning.render.shadowMapHigh);
    Object.assign(this.sun.shadow.camera, { left: -S.extent, right: S.extent, top: S.extent, bottom: -S.extent, near: 1, far: 90 });
    this.sun.shadow.normalBias = S.normalBias;
    this.sun.shadow.radius = S.radius;
    this.sun.shadow.intensity = S.intensity;
    this.skyColor = { value: new THREE.Color() };
    this.horizonColor = { value: new THREE.Color() };
    const mat = new THREE.ShaderMaterial({ side: THREE.BackSide, depthWrite: false, fog: false, uniforms: { top: this.skyColor, horizon: this.horizonColor },
      vertexShader: 'varying vec3 vP; void main() { vP = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: 'uniform vec3 top; uniform vec3 horizon; varying vec3 vP; void main() { gl_FragColor = vec4(mix(horizon, top, smoothstep(-0.05, 0.55, vP.y)), 1.0); }' });
    this.sky = new THREE.Mesh(new THREE.SphereGeometry(400, 32, 16), mat);   // look-demo town.ts sky()
    scene.add(this.hemi, this.sun, this.sun.target, this.sky);
    scene.fog = new THREE.Fog(PRESETS.midday.sky, L.fog.near, L.fog.far);
  }
  /** `grade`: the title's own pass over the clock's values (title/grade.ts); the town never passes one. */
  update(minuteOfDay: number, focus?: THREE.Vector3, grade?: (l: LightValues) => LightValues): LightValues {
    const clock = lightingAt(minuteOfDay), l = grade ? grade(clock) : clock;
    this.hemi.color.copy(l.hemiSky); this.hemi.groundColor.copy(l.hemiGround); this.hemi.intensity = l.hemiIntensity;
    this.sun.color.copy(l.sun); this.sun.intensity = l.sunIntensity;
    const at = focus ?? new THREE.Vector3();
    this.sun.position.copy(at).addScaledVector(l.sunDir, 40); this.sun.target.position.copy(at);
    this.skyColor.value.copy(l.sky);
    this.horizonColor.value.copy(l.horizon);
    (this.scene.fog as THREE.Fog).color.copy(l.sky);              // "background and fog color are always the same value"
    this.scene.background = l.sky;
    windowMaterial().emissiveIntensity = l.windowGlow;
    if (focus) this.sky.position.copy(focus);
    return l;
  }
  /** Out of the scene, with the sky dome and the sun's shadow map freed (title screen, 2026-10-05: the showcase town's lighting). */
  dispose(): void {
    this.scene.remove(this.hemi, this.sun, this.sun.target, this.sky);
    this.sky.geometry.dispose();
    (this.sky.material as THREE.Material).dispose();
    this.sun.shadow.dispose();
  }
}
